import React, { useState, useEffect } from 'react';
import { Camera, Film, Star, Video, Music, Edit, Award, Clock, Users, Monitor, Layout, Smile, Zap, ArrowRight, ArrowLeft } from 'lucide-react';
import '../styles/feedback.css';
import SEO from '../components/SEO';
import { baseUrl } from '../config';



const FeedbackForm = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    projectType: '',
    basicFeedback: {
      uiSatisfaction: '',
      easeOfUse: '',
      performance: '',
      reliability: '',
      onboarding: ''
    },
    usageFrequency: '',
    teamSize: '',
    toolRatings: {},
    feedback: {},
  });

  // ✅ NEW: identity + submission state
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null = checking
  const [activeUser, setActiveUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showThankYou, setShowThankYou] = useState(false);

  // ✅ NEW: check for a logged-in session on mount, mirroring script.jsx/chatbot.jsx
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await fetch(`${baseUrl}/getUserIDByToken`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        });
        if (res.ok) {
          const data = await res.json();
          setActiveUser(data.user_id);
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        setIsAuthenticated(false);
      }
    };
    checkAuth();
  }, []);

  const basicFeedbackQuestions = {
    uiSatisfaction: "How satisfied are you with ProducersBuddy's interface?",
    easeOfUse: "How easy is it to navigate and use our software?",
    performance: "How would you rate the software's performance?",
    reliability: "How reliable is the software during your work?",
    onboarding: "How helpful was the onboarding experience?"
  };

  const frequencyOptions = [
    { id: 'daily', label: 'Daily' },
    { id: 'weekly', label: '2-3 Times a Week' },
    { id: 'monthly', label: 'Few Times a Month' },
    { id: 'occasionally', label: 'Occasionally' }
  ];

  const teamSizes = [
    { id: 'solo', label: 'Solo Creator' },
    { id: 'small', label: 'Small Team (2-5)' },
    { id: 'medium', label: 'Medium Team (6-15)' },
    { id: 'large', label: 'Large Team (15+)' }
  ];

  const projectTypes = [
    { id: 'feature', label: 'Feature Film', icon: Film },
    { id: 'short', label: 'Short Film', icon: Video },
    { id: 'commercial', label: 'Commercial', icon: Monitor },
    { id: 'music', label: 'Music Video', icon: Music },
    { id: 'documentary', label: 'Documentary', icon: Camera }
  ];

  const emojis = [
    { rating: 1, emoji: '😢', label: 'Very Unsatisfied' },
    { rating: 2, emoji: '😕', label: 'Unsatisfied' },
    { rating: 3, emoji: '😐', label: 'Neutral' },
    { rating: 4, emoji: '😊', label: 'Satisfied' },
    { rating: 5, emoji: '🤩', label: 'Very Satisfied' }
  ];

  const tools = {
    'Pre-Production': {
      'Script Analysis': '',
      'Budgeting': '',
      'Casting Assistant': '',
      'Location': ''
    },
    'Production': {
      'Progress Management': '',
      'Cast Management': '',
      'Scheduling Assistant': '',
      'Documentation': ''
    },
    'Post-Production': {
      'Editing': '',
      'Visual Effects': '',
      'Marketing Analyst': '',
      'Advertisements': '',
      'Posters': '',
      'Trailers': '',
      'Distributions': ''
    }
  };

  const steps = [
    { number: 1, title: "Select Project Type" },
    { number: 2, title: "General Software Feedback" },
    { number: 3, title: "AI Tools Rating" }
  ];

  const handleBasicFeedback = (aspect, rating) => {
    setFormData(prev => ({
      ...prev,
      basicFeedback: {
        ...prev.basicFeedback,
        [aspect]: rating
      }
    }));
  };

  const handleToolRating = (category, tool, rating) => {
    setFormData(prev => ({
      ...prev,
      toolRatings: {
        ...prev.toolRatings,
        [`${category}-${tool}`]: rating
      }
    }));
  };
  // ✅ NEW: merge the two parallel tool dicts into the per-tool {rating, comment} shape
  const buildToolRatingsPayload = () => {
    const keys = new Set([
      ...Object.keys(formData.toolRatings),
      ...Object.keys(formData.feedback)
    ]);
    if (keys.size === 0) return null;
    const merged = {};
    keys.forEach((key) => {
      merged[key] = {
        rating: formData.toolRatings[key] || null,
        comment: formData.feedback[key] || null
      };
    });
    return merged;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      setSubmitError('Please log in to submit feedback.');
      return;
    }
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const response = await fetch(`${baseUrl}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          project_type: formData.projectType || null,
          ui_satisfaction: formData.basicFeedback.uiSatisfaction || null,
          ease_of_use: formData.basicFeedback.easeOfUse || null,
          performance: formData.basicFeedback.performance || null,
          reliability: formData.basicFeedback.reliability || null,
          onboarding: formData.basicFeedback.onboarding || null,
          usage_frequency: formData.usageFrequency || null,
          team_size: formData.teamSize || null,
          tool_ratings: buildToolRatingsPayload()
        })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to submit feedback');
      }
      setShowThankYou(true);
      setTimeout(() => {
        window.location.href = '/';
      }, 5000);
    } catch (error) {
      setSubmitError(error.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showThankYou) {
    return (
      <div className="min-h-screens flex items-center justify-center bg-white">
        <h1 className="text-4xl font-bold text-grays-800">Thank you for your valuable feedback on ProducersBuddy!</h1>
      </div>
    );
  }

  const renderStepIndicator = () => (
    <div className="flex justify-center mb-8">
      <div className="flex items-center space-x-4">
        {steps.map((step, index) => (
          <React.Fragment key={step.number}>
            <div className="flex flex-col items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 
                ${currentStep >= step.number ? 'border-blue-400 bg-blue-400 text-white' : 'border-gray-500 text-gray-500'}`}>
                {step.number}
              </div>
              <span className={`text-sm mt-2 ${currentStep >= step.number ? 'text-blue-400' : 'text-gray-500'}`}>
                {step.title}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div className={`w-20 h-0.5 mt-5 ${currentStep > step.number ? 'bg-blue-400' : 'bg-gray-500'}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  const renderNavigationButtons = () => (
    <div className="flex justify-center space-x-4 pt-6">
      {currentStep > 1 && (
        <button
          onClick={() => setCurrentStep(prev => prev - 1)}
          className="px-6 py-3 bg-gray-700 text-white rounded-lg font-semibold 
            transform transition-all hover:scale-105 focus:outline-none focus:ring-2 
            flex items-center space-x-2">
          <ArrowLeft className="w-5 h-5" />
          <span>Previous</span>
        </button>
      )}
      {currentStep < 3 && (
        <button
          onClick={() => setCurrentStep(prev => prev + 1)}
          className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg 
            font-semibold transform transition-all hover:scale-105 focus:outline-none focus:ring-2 
            flex items-center space-x-2">
          <span>Next</span>
          <ArrowRight className="w-5 h-5" />
        </button>
      )}
      {currentStep === 3 && (
        <div className="flex flex-col items-center space-y-2">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || isAuthenticated !== true}
            className="px-8 py-4 bg-gradient-to-r from-green-500 to-blue-600 text-white rounded-lg
              font-semibold transform transition-all hover:scale-105 focus:outline-none focus:ring-2
              disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100">
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
          {isAuthenticated === false && (
            <p className="text-red-400 text-sm">Please log in to submit feedback.</p>
          )}
          {submitError && (
            <p className="text-red-400 text-sm">{submitError}</p>
          )}
        </div>
      )}
    </div>
  );

  const renderStep1 = () => (
    <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 shadow-xl">
      <h2 className="text-xl font-semibold mb-6 flex items-center">
        <Award className="w-6 h-6 mr-2 text-blue-400" />
        What type of projects do you primarily work on?
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {projectTypes.map(({ id, label, icon: Icon }) => (
          <div
            key={id}
            onClick={() => setFormData(prev => ({ ...prev, projectType: id }))}
            className={`cursor-pointer p-4 rounded-lg transition-all duration-300 transform hover:scale-105 
              ${formData.projectType === id ? 'bg-blue-600 shadow-lg shadow-blue-500/50' : 'bg-gray-700 hover:bg-gray-600'}`}>
            <div className="flex items-center space-x-3">
              <Icon className="w-6 h-6" />
              <span>{label}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 shadow-xl">
        <h2 className="text-xl font-semibold mb-6 flex items-center">
          <Layout className="w-6 h-6 mr-2 text-blue-400" />
          Overall Software Experience
        </h2>
        <div className="space-y-6">
          {Object.entries(basicFeedbackQuestions).map(([aspect, question]) => (
            <div key={aspect} className="space-y-2">
              <label className="block text-lg mb-2">{question}</label>
              <div className="flex justify-between items-center bg-gray-700 p-4 rounded-lg">
                {emojis.map(({ rating, emoji, label }) => (
                  <button
                    key={rating}
                    onClick={() => handleBasicFeedback(aspect, rating)}
                    className={`text-2xl transform transition-transform hover:scale-125 
                      ${formData.basicFeedback[aspect] === rating ? 'scale-125' : ''}`}
                    title={label}>
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 shadow-xl">
        <h2 className="text-xl font-semibold mb-6 flex items-center">
          <Clock className="w-6 h-6 mr-2 text-blue-400" />
          How often do you use ProducersBuddy?
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {frequencyOptions.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setFormData(prev => ({ ...prev, usageFrequency: id }))}
              className={`p-3 rounded-lg transition-all ${
                formData.usageFrequency === id
                  ? 'bg-blue-600 shadow-lg shadow-blue-500/50'
                  : 'bg-gray-700 hover:bg-gray-600'
              }`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 shadow-xl">
        <h2 className="text-xl font-semibold mb-6 flex items-center">
          <Users className="w-6 h-6 mr-2 text-blue-400" />
          What's your team size?
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {teamSizes.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setFormData(prev => ({ ...prev, teamSize: id }))}
              className={`p-3 rounded-lg transition-all ${
                formData.teamSize === id
                  ? 'bg-blue-600 shadow-lg shadow-blue-500/50'
                  : 'bg-gray-700 hover:bg-gray-600'
              }`}>
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      {Object.entries(tools).map(([category, categoryTools]) => (
        <div key={category} className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 shadow-xl">
          <h2 className="text-xl font-semibold mb-6 text-blue-400">{category}</h2>
          <div className="space-y-6">
            {Object.keys(categoryTools).map(tool => (
              <div key={tool} className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg">{tool}</span>
                  <div className="flex space-x-4">
                    {emojis.map(({ rating, emoji, label }) => (
                      <button
                        key={rating}
                        onClick={() => handleToolRating(category, tool, rating)}
                        className={`text-2xl transform transition-transform hover:scale-125 
                          ${formData.toolRatings[`${category}-${tool}`] === rating ? 'scale-125' : ''}`}
                        title={label}>
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
                {formData.toolRatings[`${category}-${tool}`] && (
                  <textarea
                    className="w-full p-4 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-blue-500"
                    placeholder={
                      formData.toolRatings[`${category}-${tool}`] >= 4
                        ? "What did you love most about this tool?"
                        : "What areas need improvement?"
                    }
                    rows="3"
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      feedback: {
                        ...prev.feedback,
                        [`${category}-${tool}`]: e.target.value
                      }
                    }))}
                    value={formData.feedback[`${category}-${tool}`] || ''}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screens bg-gradient-to-b from-gray-900 to-gray-800 text-white py-8 px-4">
      <SEO
        title="Share Your Feedback"
        description="Help us improve ProducerBuddy by sharing your feedback. We value your experience as a filmmaker."
        url="https://producerbuddy.pilomation.com/feedback"
        robots="noindex, nofollow"
      />
      <div className="text-center mb-12 space-y-4">
        <div className="flex justify-center mb-4">
          <Film className="w-16 h-16 text-blue-400 animate-pulse" />
        </div>
        <h1 className="text-4xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500">
          ProducersBuddy Feedback
        </h1>
        <p className="text-gray-300 max-w-2xl mx-auto">
          Help us improve your AI-powered production experience
        </p>
      </div>

      {renderStepIndicator()}

      <div className="max-w-4xl mx-auto">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {renderNavigationButtons()}
      </div>
    </div>
  );
};

export default FeedbackForm;
